import ApiService from '@/services/apiService';
import LambdaApiService from '@/services/apiService/lambda';
import { mapGetters, mapState } from 'vuex';
import cloneDeep from 'lodash/cloneDeep';
import get from 'lodash/get';
import has from 'lodash/has';
import isEqual from 'lodash/isEqual';
import { codemirror } from 'vue-codemirror';
import 'codemirror/lib/codemirror.css';
import 'codemirror/theme/base16-dark.css';
import 'codemirror/theme/duotone-light.css';
import 'codemirror/mode/javascript/javascript.js';
import { getColumnSize } from '@/utils/bridgeUtils';
export default {
  name: 'CoreConnectorsCreateDialog',
  components: {
    codemirror
  },
  props: {
    isOpen: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      cmOptions: {
        tabSize: 4,
        mode: {
          name: 'javascript',
          json: true
        },
        firstLineNumber: 0,
        theme: this.$store.state.app.UiTheme ? 'base16-dark' : 'duotone-light',
        lineNumbers: false,
        line: true,
        foldGutter: true,
        styleActiveLine: true,
        styleSelectedText: true,
        hintOptions: {
          completeSingle: false
        },
        lineWrapping: false
      },
      connectorId: parseInt((this.$route.params || {}).id) || null,
      selectedAccount: null,
      accounts: [],
      connector: {},
      bridgeValidation: {},
      allBridgeTypes: [],
      bridgeTypes: [],
      lastSavedBridge: {}
    };
  },
  computed: {
    ...mapState('app', ['color', 'inpOutlined', 'inpFilled']),
    ...mapGetters({
      isFeatureFlagEnabled: 'app/isFeatureFlagEnabled'
    }),
    canSave() {
      return this.connector.type !== null && this.connector.name && (this.selectedAccount || {}).id;
    },
    hasConnectionDataError() {
      return get(this.bridgeValidation, 'data.length', 0) > 0;
    }
  },
  watch: {
    isOpen(newVal) {
      if (newVal) {
        this.setupNewConnector();
      }
    },
    'connector.type': {
      handler(newVal) {
        const bridgeTypesItem = this.bridgeTypes.find(config => config.type === newVal);
        if (bridgeTypesItem !== undefined && has(bridgeTypesItem.manifest, 'templates') && bridgeTypesItem.manifest.templates.length > 0) {
          bridgeTypesItem.manifest.templates.forEach(item => {
            if (item.default === true) {
              this.connector.config.parser.script = item.name;
            } else {
              this.connector.config.parser.script = bridgeTypesItem.manifest.templates[0].name;
            }
          });
        }
      },
      deep: true
    },
    selectedAccount(selectedAccount) {
      this.loadTenantModules(selectedAccount.id).then(tenantModules => {
        const wmBusModule = tenantModules.find(m => m.key === 'wMBus');
        const wmBusModuleEnabled = wmBusModule ? wmBusModule.isEnabled : false;
        const lwm2mModule = tenantModules.find(m => m.key === 'LwM2M');
        const lwm2mModuleEnabled = lwm2mModule ? lwm2mModule.isEnabled : false;
        this.bridgeTypes = this.allBridgeTypes;
        if (!wmBusModuleEnabled) {
          this.bridgeTypes = this.bridgeTypes.filter(bridge => {
            return bridge.type !== 'bridge-wireless-mbus-decoder';
          });
        }
        if (!lwm2mModuleEnabled) {
          this.bridgeTypes = this.bridgeTypes.filter(bridge => {
            return bridge.type !== 'bridge-lwm2m-server';
          });
        }
        if (!this.bridgeTypes.some(bt => bt.type === this.connector.type)) {
          this.connector.type = null;
        }
      }).catch(error => {
        console.error(error);
        this.$snack('Error loading data', 'error');
      });
    }
  },
  mounted() {
    this.fetchAccounts();
    this.loadData().then(() => {
      this.setupNewConnector();
    }).catch(error => {
      console.error(error);
      this.$snack('Error loading data', 'error');
    });
  },
  methods: {
    startMicrosoftOAuthProcess(bridgeConfigId, clientId, tenantId) {
      const authorizationUrl = `https://login.microsoftonline.com/${tenantId}/oauth2/v2.0/authorize` + `?client_id=${clientId}` + '&response_type=code' + `&redirect_uri=${location.origin}/api/v1/oauth2/office365/callback` + '&response_mode=query' + `&scope=${encodeURIComponent('https://outlook.office365.com/SMTP.Send offline_access')}` + `&state=${bridgeConfigId},${window.location.protocol}//${window.location.hostname}`;
      // Redirect the browser
      window.location.href = authorizationUrl;
    },
    get,
    getColumnSize,
    setupNewConnector() {
      this.bridgeValidations = {};
      this.connector = {
        name: this.$i18n_t('accMgmt.tenant.bridges.newBridge'),
        type: null,
        bridge: {
          config: {}
        },
        config: {
          parser: {}
        }
      };
    },
    fetchAccounts() {
      this.$store.dispatch('app/loadAccounts').then(accounts => {
        this.accounts = accounts;
      }).catch(error => {
        console.error(error);
        this.$snack(this.$i18n_t('NiotaClassic.Devices.snacks.errorFindingAccounts'), 'error');
      });
    },
    loadData() {
      this.isLoadingConnectors = true;
      this.bridgeTypes = [];
      return Promise.all([this.loadBridgeTypes()]).then(([types]) => {
        this.bridgeTypes = types.map(type => {
          return {
            ...type,
            name: get(type, 'manifest.displayName') || type.type
          };
        }).sort((b1, b2) => {
          if (b1.name > b2.name) {
            return 1;
          }
          if (b1.name < b2.name) {
            return -1;
          }
          return 0;
        });
        this.allBridgeTypes = cloneDeep(this.bridgeTypes);
        if (this.$route.query.showBridges !== undefined) {
          this.scrollBridgesIntoView();
        }
      }).catch(error => {
        console.error(error);
        this.$snack(this.$i18n_t('Common.errorLoadingData'), 'error');
      }).finally(() => {
        this.isLoadingConnectors = false;
      });
    },
    loadBridgeTypes() {
      return ApiService.getBridgeTypes();
    },
    bridgeConfigIsInbound(bridge) {
      return get(bridge, 'manifest.functionScope.invokeDirection.inbound', false);
    },
    bridgeConfigIsOutbound(bridge) {
      return get(bridge, 'manifest.functionScope.invokeDirection.outbound', false);
    },
    bridgeConfig(bridge) {
      return this.bridgeTypes.find(config => config.type === bridge.type);
    },
    getBridgeTypeByType(type) {
      return this.bridgeTypes.find(item => item.type === type);
    },
    getBridgeTypeName(type) {
      const bridgeType = this.getBridgeTypeByType(type);
      if (!bridgeType) {
        return type || '';
      }
      return bridgeType.name || bridgeType.type;
    },
    validTemplates(templates) {
      if (!Array.isArray(templates)) {
        return [];
      }
      return templates.filter(item => item && typeof item.name === 'string' && item.name.length > 0);
    },
    hasQuickReferenceContent(bridgeInstance) {
      const quickReference = get(this.bridgeConfig(bridgeInstance), 'manifest.functionScope.quickReference');
      if (!quickReference) {
        return false;
      }
      const keys = ['title', 'text01', 'text02', 'text03'];
      return keys.some(key => this.$i18n_te(`Bridges.quickReferences.${quickReference}.${key}`));
    },
    loadTenantModules(tenantId) {
      return ApiService.tenantFetchModules(tenantId);
    },
    bridgeConfigKeys(instance) {
      const bridgeManifest = this.bridgeConfig(instance).manifest;
      let keys = Object.keys(bridgeManifest.config[0]).filter(key => {
        if (get(bridgeManifest, `config[0][${key}].hidden`)) {
          return false;
        }

        // if field is readonly, only show if it has a value
        if (get(bridgeManifest, `config[0][${key}].readonly`)) {
          return !!instance.bridge.config[key];
        }
        const visibleConfig = get(bridgeManifest, `config[0][${key}].visible`) || {};
        if (!Object.keys(visibleConfig).every(dependentKey => {
          return instance.bridge.config[dependentKey] === visibleConfig[dependentKey];
        })) {
          return false;
        }
        const notVisibleConfig = get(bridgeManifest, `config[0][${key}].not_visible`) || {};
        if (!Object.keys(notVisibleConfig).every(dependentKey => {
          return instance.bridge.config[dependentKey] !== notVisibleConfig[dependentKey];
        })) {
          return false;
        }
        return true;
      });
      return keys.sort((a, b) => {
        const orderIdxA = get(bridgeManifest, `config[0][${a}].orderIdx`) || 0;
        const orderIdxB = get(bridgeManifest, `config[0][${b}].orderIdx`) || 0;
        if (orderIdxA < orderIdxB) {
          return -1;
        }
        if (orderIdxA > orderIdxB) {
          return 1;
        }
        return a.localeCompare(b);
      });
    },
    bridgeHasValidParameters(strict = false) {
      let currentParameters = get(this.connector, 'bridge.config');
      let savedParameters = get(this.lastSavedBridge, 'bridge.config');
      return !get(this.bridgeValidation, 'error', strict ? true : !isEqual(currentParameters, savedParameters));
    },
    changedBridgeParams() {
      this.bridgeValidation = {};
    },
    shouldShowTemplateSelect(bridgeInstance) {
      const bridgeConfig = this.bridgeConfig(bridgeInstance);
      const templates = this.validTemplates(bridgeConfig.manifest.templates);
      return bridgeConfig.manifest.functionScope.invokeDirection.inbound && templates.length > 0 && (templates.length > 1 || bridgeConfig.manifest.functionScope.editableMutator);
    },
    editConfigMutator(bridge) {
      if (bridge.bridge.mutator) {
        delete bridge.bridge.mutator;
        this.bridgeValidation = cloneDeep(this.bridgeValidation);
        return;
      }
      const template = this.bridgeConfig(bridge).manifest.templates.find(template => {
        return template.name === bridge.config.parser.script;
      });
      bridge.bridge.mutator = get(template, 'mutator.script');
      bridge.bridge = cloneDeep(bridge.bridge);
      this.bridgeValidation = cloneDeep(this.bridgeValidation);
    },
    refreshForm() {
      setTimeout(() => {
        // required workaround to get the cursor appearing at the right spot
        // https://discuss.codemirror.net/t/codemirror-cursor-is-not-partially-visible/648/6
        this.$refs['codemirror'][0].codemirror.refresh();
        this.$refs['codemirror'][0].codemirror.focus();
      }, 50);
    },
    onCmFocus() {
      this.refreshForm();
      this.cmOptions.lineNumbers = true;
    },
    validateConnector() {
      const scriptToEval = this.connector.bridge.mutator;
      const promiseValidate = ApiService.validateBridge(this.connector.type, this.connector.id, this.connector.bridge.config);
      const promiseEvalMutator = scriptToEval ? LambdaApiService.evaluateScript({
        script: scriptToEval,
        scriptParams: [{}]
      }) : Promise.resolve({});
      Promise.all([promiseValidate, promiseEvalMutator]).then(([validationResult, mutatorResult]) => {
        const bridgeInstance = this.connector;
        const bridgeManifest = this.bridgeConfig(bridgeInstance).manifest;
        if (get(bridgeManifest, 'functionScope.validator.returnsConfig')) {
          // (parts of) the config is returned/calculated for this bridge-validator,
          // so overwrite/merge with the existing config
          bridgeInstance.bridge.config = {
            ...bridgeInstance.bridge.config,
            ...validationResult.data
          };
        }
        let errorMessage = get(validationResult, 'errorMessage', get(mutatorResult, 'error'));
        if (this.connector.bridge.config.service !== 'Outlook365(OAuth2.0)') {
          this.bridgeValidation = validationResult;
          this.connector.hasError = true;
        }
        if (mutatorResult.syntaxError) {
          this.bridgeValidation = {
            ...mutatorResult,
            mutatorScriptError: true
          };
        }
        if (errorMessage) {
          this.$snack(errorMessage, 'error');
        } else {
          this.persistBridge();
        }
      }).catch(error => {
        console.error(error);
      });
    },
    persistBridge() {
      let persistPromise = Promise.resolve();
      if (this.connector.id) {
        persistPromise = ApiService.updateBridge(this.connector.id, this.connector);
      } else {
        persistPromise = ApiService.createBridge(this.selectedAccount.id, this.connector.type, this.connector);
      }
      return persistPromise.then(async persistedBridge => {
        this.connector = persistedBridge;
        this.lastSavedBridge = persistedBridge;
        const {
          client_id,
          tenant_id,
          service
        } = persistedBridge.bridge.config;
        this.$snack(this.$i18n_t('Connectors.Connectors.saveSuccess'), 'success');
        if (service === 'Outlook365(OAuth2.0)') {
          return this.startMicrosoftOAuthProcess(persistedBridge.id, client_id, tenant_id);
        }
        this.$emit('close', true);
      }).catch(error => {
        if (error.response && this.$i18n_t(`Error.${error.response.data.message}`)) {
          this.$snack(this.$i18n_t(`Error.${error.response.data.message}`), 'error');
        } else {
          this.$snack(`${error.response.data.message}`, 'error');
        }
      });
    },
    resetBridgeState() {
      ApiService.resetBridgeState(this.connector.id, 'waiting').then(persistedBridge => {
        this.connector = persistedBridge;
      }).catch(error => {
        console.error(error);
        this.$snack(error, 'error');
      });
    }
  }
};